'use client';

import { ImageIcon } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { useState } from 'react';

import { cx } from '@/utils';
import getImage from '@/utils/getImageData';

export default function SimilarBlogs({ post, aspect }) {
  const [isHovered, setIsHovered] = useState(false);
  const imageProps = getImage(post.mainImage, {
    width: 600,
    height: 600,
  });

  const authorName = post?.author?.name || 'AllBlogs Author';
  const imageAlt = `${post.title} by ${authorName} on AllBlogs`;

  // Determine if we have a valid category
  const hasCategory = post.categories && post.categories.length > 0;
  const categoryName = hasCategory
    ? typeof post.categories[0] === 'string'
      ? post.categories[0]
      : post.categories[0].title
    : null;

  return (
    <div
      className="group relative overflow-hidden rounded-xl bg-white shadow-lg transition-all duration-300 hover:shadow-xl dark:bg-gray-800"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Card with glass morphism effect */}
      <div className="relative h-full w-full backdrop-blur-sm">
        {/* Image container */}
        <div
          className={cx(
            'relative overflow-hidden',
            aspect === 'landscape' ? 'aspect-video' : 'aspect-square',
          )}
        >
          <Link
            prefetch={false}
            title={post.title}
            href={`/post/${post.slug}`}
            className="relative block h-full w-full"
          >
            {imageProps?.src ? (
              <>
                {/* Premium gradient overlay with animation */}
                <div
                  className={cx(
                    'absolute inset-0 z-10 bg-gradient-to-t from-gray-900 via-gray-900/70 to-transparent transition-opacity duration-300',
                    isHovered ? 'opacity-70' : 'opacity-80',
                  )}
                />

                <Image
                  unoptimized
                  src={imageProps.src}
                  blurDataURL={post.blurDataURL || imageProps.blurDataURL}
                  placeholder="blur"
                  fill
                  className={cx(
                    'object-cover transition-all duration-700 ease-in-out',
                    isHovered ? 'scale-110' : 'scale-100',
                  )}
                  title={imageAlt}
                  alt={imageAlt}
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                />
              </>
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-gray-100 dark:bg-gray-800">
                <ImageIcon className="h-16 w-16 text-gray-300 dark:text-gray-600" />
              </div>
            )}

            {/* Enhanced category tag with glow effect */}
            {hasCategory && (
              <span className="absolute top-3 left-3 z-20 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 px-3 py-1 text-xs font-medium text-white shadow-lg transition-all duration-300 group-hover:shadow-blue-500/30">
                {categoryName}
              </span>
            )}

            {/* Title with overlay effect */}
            <div className="absolute right-0 bottom-0 left-0 z-20 p-4">
              <h3 className="line-clamp-2 text-base font-bold tracking-tight text-white transition-colors duration-300 group-hover:text-blue-300 md:text-lg">
                {post.title}
              </h3>

              {/* Author info */}
              {post.author && (
                <div className="mt-2 flex items-center">
                  <span className="text-xs text-gray-300">By {post.author.name}</span>
                </div>
              )}
            </div>
          </Link>
        </div>

        {/* Content section with enhanced typography - Only show on aspect != square */}
        {aspect !== 'square' && (
          <div className="p-4">
            <Link prefetch={false} href={`/post/${post.slug}`}>
              <p className="mb-3 line-clamp-2 text-sm text-gray-600 dark:text-gray-300">
                {post.excerpt || 'Read this interesting article on AllBlogs'}
              </p>
            </Link>

            {/* Read more link with arrow animation */}
            <Link
              prefetch={false}
              href={`/post/${post.slug}`}
              className="mt-2 inline-flex items-center text-sm font-medium text-blue-600 transition-all duration-300 hover:translate-x-1 dark:text-blue-400"
            >
              Read Article
              <svg className="ml-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M14 5l7 7m0 0l-7 7m7-7H3"
                />
              </svg>
            </Link>
          </div>
        )}
      </div>

      {/* Hover effect card decoration */}
      <div className="absolute inset-0 -z-10 rounded-xl bg-gradient-to-tr from-blue-600/20 to-purple-600/20 opacity-0 blur-xl transition-opacity duration-300 group-hover:opacity-100"></div>
    </div>
  );
}
